/**
 * The frame every screen sits in. It is two frames, and which one you get is the width of the
 * glass you are holding.
 *
 * ON A PHONE the navigation is at the BOTTOM. This is used one-handed, in a car, between two
 * houses, so the things you tap belong under a thumb and not under the notch. That layout came
 * first and it is not spent to buy anything on a desk: the phone header, the bottom bar and every
 * 44px target below the md breakpoint are exactly what they were.
 *
 * ON A DESK, from md up, the bottom bar is replaced by a rail down the left with the same words
 * plus every one the bar has no room for, and the navy header by a 72px top bar carrying the
 * screen's own title, who is signed in, and the bell. The rail is 248px because a labelled word is
 * worth more every day than the 176px an icon-only rail would give back to a table.
 *
 * THE TITLE IN THE TOP BAR IS THE SCREEN'S OWN. `ScreenTitle` publishes it through a context and
 * hides itself at md, so no screen had to be edited to move its heading into the frame, and a
 * screen rendered without this shell still draws its heading where it always did.
 *
 * `nav` is passed in rather than decided here, because what a person may reach is decided once, in
 * App.tsx, from the role the server reported. A cleaner passes an empty list and gets neither bar
 * nor rail at any width: one screen needs no navigation, only a way back.
 */
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import type { Me } from '../api.ts';

export interface NavItem {
  readonly to: string;
  readonly label: string;
  /** Drawn in the rail only. The phone's bottom bar has room for five words and no more. */
  readonly railOnly?: boolean;
  /** Pinned under the rule at the foot of the rail rather than in the daily list. */
  readonly foot?: boolean;
  /**
   * Other paths this word is lit for, each with everything under it. A word is lit for its own
   * path and everything under that already; this is for a tab of the same screen that lives
   * somewhere else, as the texts live at `/messages` beside the follow-ups at `/tasks`.
   */
  readonly also?: readonly string[];
}

/** Whether the page is on one of a word's other paths, or under one. */
function onAlsoPath(item: NavItem, pathname: string): boolean {
  return (item.also ?? []).some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/**
 * A door to another system we run, drawn in the top bar at every width and opened in a new tab.
 * The phone system's live call wall is the first: it is its own Worker with its own sign-in, and
 * an office person wants it one press away from here without losing her place in this one.
 */
export interface ExternalLink {
  readonly href: string;
  readonly label: string;
  /** Said aloud by a screen reader and shown on hover: what is behind the door, in a sentence. */
  readonly hint: string;
}

/**
 * THE WIDEST THE PAGE GROWS BESIDE THE RAIL (ops #56), and the top bar's words with it. Up to it
 * the page takes all the room the rail leaves, as it always did; past it, on a 1920, 2560 or 3840
 * wide monitor, it stops at `--container-desk` in styles.css and stands in the middle of that room,
 * so a table row or a sentence is not stretched across the whole glass. Below md the phone's
 * `max-w-3xl` column is untouched.
 */
export const DESK_WIDTH = 'md:max-w-desk';

/**
 * THE WIDTH OF A SCREEN THAT IS READ AND FILLED IN RATHER THAN SCANNED (ops #56): Settings, Crew
 * and an invoice are sentences, forms and a column of figures, not a table. The desk page lets
 * them run 1,550px wide on a monitor, which put a sentence on one line of 200 characters and an
 * amount a whole screen away from the words it belongs to. They stop at the phone's own 48rem and
 * stand at the left, under the title in the bar; a table screen keeps the whole page.
 */
export const READING_WIDTH = 'max-w-3xl';

/**
 * THE MEASURE OF A SENTENCE ON A TABLE SCREEN (ops #56). A table screen keeps the whole desk page,
 * because a table's columns want the room; the sentences around it do not. At 1920 the notes under
 * the Sources table ran 286 characters on one line and the Promises note on the schedule 161. So a
 * note that explains a table, a field's hint and an empty list's words stop at 65 characters of
 * their own type, about 75 of prose, and the table beside them keeps its width. Below that width
 * the column is already narrower, so a phone is untouched.
 */
export const MEASURE = 'max-w-prose';

/** What the top bar shows on the left. Published by the screen, not decided here. */
interface Heading {
  readonly title: string;
  readonly subtitle?: string;
}

/**
 * Null when this shell draws no top bar, which is how `ScreenTitle` knows to draw itself at every
 * width instead of hiding at md and handing the words upward.
 */
const HeadingSlot = createContext<Dispatch<SetStateAction<Heading | null>> | null>(null);

export function AppShell({
  me,
  nav,
  unread,
  counts,
  links,
  systems,
  banner,
  children,
}: {
  me: Me;
  nav: readonly NavItem[];
  /** Unread notifications, counted by App.tsx once at boot and again on every route change. */
  unread: number;
  /** Other systems of ours, one press away in the top bar. None for a cleaner. */
  links?: readonly ExternalLink[];
  /**
   * The rest of the other systems, behind one Systems menu beside those doors: what `GET /api/me`
   * hands an owner or a manager, and nothing for anybody else. No menu is drawn with none.
   */
  systems?: readonly ExternalLink[];
  /**
   * A number beside a rail item, keyed by its path. Given only where an endpoint already answers
   * it cheaply: a badge that costs a request on every route change is not worth the digit.
   */
  counts?: Readonly<Record<string, number | undefined>>;
  /**
   * Drawn once over every screen of the frame: the "Ringing now" banner, handed by App.tsx in the
   * office branch alone (ops #84). The frame decides nothing about who gets it.
   */
  banner?: ReactNode;
  children: ReactNode;
}) {
  const [heading, setHeading] = useState<Heading | null>(null);
  const railed = nav.length > 0;
  const menu = systems && systems.length > 0 ? systems : null;
  const barItems = nav.filter((item) => !item.railOnly && !item.foot);
  const { pathname } = useLocation();

  return (
    <HeadingSlot.Provider value={railed ? setHeading : null}>
      <div className={`min-h-dvh bg-cream text-navy ${railed ? 'md:flex' : ''}`}>
        {railed ? <Rail me={me} nav={nav} counts={counts} /> : null}

        <div className="flex min-w-0 flex-1 flex-col">
          <header
            className={`sticky top-0 z-10 bg-navy text-cream shadow-sm ${railed ? 'md:hidden' : ''}`}
          >
            <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-2">
              <div className="flex items-center gap-3">
                <img
                  src="/logo-mark-reverse.svg"
                  alt=""
                  width={320}
                  height={166}
                  className="h-7 w-auto"
                />
                <div>
                  <p className="font-serif text-lg leading-tight">Faithfully Kept</p>
                  <p className="text-xs tracking-wide text-cream/70">Operations</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm leading-tight">{me.displayName}</p>
                  <p className="text-xs text-cream/70">{me.roleLabel}</p>
                </div>
                {/* ON A PHONE THE DOORS FOLD INTO THE MENU where there is one. The logo, the name,
                    a door, the menu and the bell, at their narrowest, want more than the 358px a
                    390px phone leaves this bar, so an owner's phone draws one square holding the
                    Call wall first and the rest after it, and the bar is as wide as the office's. */}
                {menu ? (
                  <SystemsMenu links={[...(links ?? []), ...menu]} place="phone" />
                ) : (
                  links?.map((link) => (
                    <OutsideDoor key={link.href} link={link} tone="navy" iconOnly />
                  ))
                )}
                <Bell unread={unread} tone="navy" />
              </div>
            </div>
          </header>

          {railed ? (
            <header className="sticky top-0 z-10 hidden h-18 shrink-0 border-b border-navy/15 bg-cream md:block">
              {/* The bar's words stand over the page they name: the same width as <main> below and
                  centred the same way, while its rule runs the whole width of the glass. */}
              <div
                className={`mx-auto flex h-full w-full items-center justify-between gap-6 px-6 ${DESK_WIDTH}`}
              >
                {/* THE TITLE HAS THE ROOM ON A TABLET (ops #56). Beside the rail at 768 the bar is
                    520px, and the title, the sentence under it, the Call wall door, the name and
                    the bell cut the sentence to three words and the title to two. Below lg the
                    door is its mark alone, the sentence may take a second line, and the name and
                    the role step down to the foot of the rail, where there is room for them: the
                    phone's bar that names her is gone from md, so without that she is named
                    nowhere between 768 and 1023. */}
                <div className="flex min-w-0 flex-col gap-0.5">
                  {heading ? (
                    <>
                      <h1 className="truncate font-serif text-2xl leading-tight">
                        {heading.title}
                      </h1>
                      {heading.subtitle ? (
                        <p className="line-clamp-2 text-[13px] leading-4 text-navy/70">
                          {heading.subtitle}
                        </p>
                      ) : null}
                    </>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-4">
                  {links?.map((link) => (
                    <OutsideDoor key={link.href} link={link} tone="cream" />
                  ))}
                  {menu ? <SystemsMenu links={menu} place="desk" /> : null}
                  <div className="hidden text-right lg:block">
                    <p className="text-sm leading-tight">{me.displayName}</p>
                    <p className="text-xs text-navy/60">{me.roleLabel}</p>
                  </div>
                  <Bell unread={unread} tone="cream" />
                </div>
              </div>
            </header>
          ) : null}

          <main
            className={`mx-auto w-full max-w-3xl px-4 pt-5 ${barItems.length > 0 ? 'pb-32' : 'pb-12'} ${
              railed ? `md:px-6 md:pb-6 ${DESK_WIDTH}` : ''
            }`}
          >
            {children}
          </main>
        </div>

        {banner}

        {barItems.length > 0 ? (
          <nav className="fixed inset-x-0 bottom-0 z-10 border-t border-navy/15 bg-cream/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
            <ul className="mx-auto flex max-w-3xl">
              {barItems.map((item) => (
                <li key={item.to} className="flex-1">
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      [
                        'flex min-h-14 items-center justify-center px-2 text-center text-sm font-medium',
                        isActive || onAlsoPath(item, pathname)
                          ? 'border-t-2 border-gold text-navy'
                          : 'border-t-2 border-transparent text-navy/60',
                      ].join(' ')
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
      </div>
    </HeadingSlot.Provider>
  );
}

/* ------------------------------------------------------------------ *
 * The rail, at md and above.
 * ------------------------------------------------------------------ */

function Rail({
  me,
  nav,
  counts,
}: {
  me: Me;
  nav: readonly NavItem[];
  counts?: Readonly<Record<string, number | undefined>>;
}) {
  const daily = nav.filter((item) => !item.foot);
  const foot = nav.filter((item) => item.foot);

  return (
    <aside className="sticky top-0 hidden h-dvh w-62 shrink-0 flex-col self-start bg-navy text-cream md:flex">
      <div className="flex items-center gap-3 border-b border-cream/15 px-4 pt-4 pb-3.5">
        <img src="/logo-mark-reverse.svg" alt="" width={320} height={166} className="h-7 w-auto" />
        <div>
          <p className="font-serif text-lg leading-tight">Faithfully Kept</p>
          <p className="text-xs tracking-wide text-cream/70">Operations</p>
        </div>
      </div>

      <nav className="flex flex-col gap-0.5 px-2.5 py-3">
        {daily.map((item) => (
          <RailLink key={item.to} item={item} count={counts?.[item.to]} />
        ))}
      </nav>

      <div className="grow" />

      {foot.length > 0 ? (
        <div className="flex flex-col gap-0.5 border-t border-cream/15 p-2.5">
          {foot.map((item) => (
            <RailLink key={item.to} item={item} count={counts?.[item.to]} />
          ))}
        </div>
      ) : null}

      {/* Who is signed in, from md to lg only: the top bar has no room for her there, and from lg
          it names her beside the bell. */}
      <div className="border-t border-cream/15 px-4 py-3 lg:hidden">
        <p className="text-sm leading-tight">{me.displayName}</p>
        <p className="text-xs text-cream/70">{me.roleLabel}</p>
      </div>
    </aside>
  );
}

/** One word in the rail. The 3px gold edge on the left is what says you are on it. */
function RailLink({ item, count }: { item: NavItem; count?: number }) {
  const { pathname } = useLocation();
  return (
    <NavLink
      to={item.to}
      aria-label={count === undefined ? undefined : `${item.label}, ${count}`}
      className={({ isActive }) =>
        [
          'flex min-h-11 desk:min-h-9 items-center gap-3 rounded-lg border-l-[3px] pr-3 pl-[9px] text-sm font-medium',
          isActive || onAlsoPath(item, pathname)
            ? 'border-gold bg-cream/8 text-cream'
            : 'border-transparent text-cream/72',
        ].join(' ')
      }
    >
      <RailIcon to={item.to} />
      <span className="grow">{item.label}</span>
      {count === undefined ? null : (
        <span aria-hidden="true" className="text-xs text-cream/50">
          {count}
        </span>
      )}
    </NavLink>
  );
}

/**
 * A door to another system of ours, in the top bar. It opens in a new tab so the person keeps her
 * place here, and it says so to a screen reader. On the phone's navy bar it is the mark alone in a
 * 44px square; on the desk's cream bar it carries its word. Quiet: it is a door, not a gap.
 */
function OutsideDoor({
  link,
  tone,
  iconOnly = false,
}: {
  link: ExternalLink;
  tone: 'navy' | 'cream';
  iconOnly?: boolean;
}) {
  const onNavy = tone === 'navy';
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      title={link.hint}
      aria-label={`${link.label}, opens in a new tab`}
      className={[
        'flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-lg text-sm font-medium desk:min-h-9',
        iconOnly ? 'w-11' : 'border px-3',
        onNavy ? 'text-cream/85 hover:bg-cream/10' : 'border-navy/20 text-navy hover:bg-navy/5',
      ].join(' ')}
    >
      <svg
        aria-hidden="true"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M11 3h6v6" />
        <path d="M17 3l-8 8" />
        <path d="M15 11v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
      </svg>
      {/* The word from lg; beside the rail on a tablet the mark alone, named by aria-label. */}
      {iconOnly ? null : <span className="hidden lg:inline">{link.label}</span>}
    </a>
  );
}

/** The words every hint ends with, which a door in the menu says once, to a screen reader. */
const NEW_TAB = 'Opens in a new tab.';

/** What is behind a door, without the new tab said a second time under its word. */
function whatIsBehind(hint: string): string {
  return hint.endsWith(NEW_TAB) ? hint.slice(0, -NEW_TAB.length).trimEnd() : hint;
}

/**
 * THE SYSTEMS MENU (the owner, 2026-10-17): the other systems an owner or a manager oversees from
 * here, the phone system's records, growth's panel and the website's leads screens, behind one word
 * beside the Call wall door. A disclosure rather than an ARIA menu: a button that says whether it is
 * open and a short list of ordinary links, each opening in a new tab and named as the Call wall is,
 * so a screen reader reads links it already knows how to follow. Escape closes it and hands the
 * focus back; a press anywhere outside it, or on a door, closes it too.
 *
 * ON A PHONE the list hangs from the navy bar itself, which is sticky and so places it, from one
 * edge of the glass less a margin to the other: a fixed width hung off a button near the right edge
 * would run off the left of a 320px phone. AT A DESK it hangs from its own button at the right of
 * the cream bar. What is drawn is UX; who is handed the list is the server's (`routes/me.ts`).
 */
function SystemsMenu({
  links,
  place,
}: {
  links: readonly ExternalLink[];
  place: 'phone' | 'desk';
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const onPhone = place === 'phone';

  useEffect(() => {
    if (!open) return;
    const away = (event: Event) => {
      if (event.target instanceof Node && box.current?.contains(event.target)) return;
      setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <div ref={box} className={onPhone ? undefined : 'relative'}>
      <button
        ref={button}
        type="button"
        aria-label="Systems"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        title="The other systems we run, each opening in a new tab"
        onClick={() => setOpen((was) => !was)}
        className={[
          'flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-lg text-sm font-medium desk:min-h-9',
          onPhone
            ? 'w-11 text-cream/85 hover:bg-cream/10'
            : 'border border-navy/20 px-3 text-navy hover:bg-navy/5',
        ].join(' ')}
      >
        {/* Four squares, the mark for "the others", on the 20px grid the door's arrow is drawn on. */}
        <svg
          aria-hidden="true"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="5.5" height="5.5" rx="1" />
          <rect x="11.5" y="3" width="5.5" height="5.5" rx="1" />
          <rect x="3" y="11.5" width="5.5" height="5.5" rx="1" />
          <rect x="11.5" y="11.5" width="5.5" height="5.5" rx="1" />
        </svg>
        {/* The word from lg, as the door beside it; below that the mark, named by aria-label. */}
        {onPhone ? null : <span className="hidden lg:inline">Systems</span>}
      </button>
      {open ? (
        <ul
          id={listId}
          className={[
            'absolute top-full z-20 flex flex-col gap-0.5 rounded-xl border border-navy/12 bg-parchment p-1.5 text-navy shadow-lg',
            onPhone ? 'inset-x-4 mt-1' : 'right-0 mt-2 w-80',
          ].join(' ')}
        >
          {links.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                title={link.hint}
                aria-label={`${link.label}, opens in a new tab`}
                onClick={() => setOpen(false)}
                className="flex min-h-11 flex-col justify-center rounded-lg px-3 py-2 hover:bg-navy/5 desk:min-h-9"
              >
                <span className="text-sm font-medium">{link.label}</span>
                <span className="text-xs text-navy/65">{whatIsBehind(link.hint)}</span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * One stroke-drawn mark per destination, on a 20px grid at 1.5 stroke, drawn inline so the rail
 * costs no request. A path this table does not know gets the plain ring rather than a gap, so a
 * route added tomorrow lines up with the others until somebody draws it.
 */
function RailIcon({ to }: { to: string }) {
  const paths = RAIL_PATHS[to];
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="h-5 w-5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths ?? <circle cx="10" cy="10" r="6.5" />}
    </svg>
  );
}

const RAIL_PATHS: Readonly<Record<string, ReactNode>> = {
  '/today': (
    <>
      <rect x="2.75" y="4.25" width="14.5" height="13" rx="2" />
      <path d="M6.5 2.75v3M13.5 2.75v3M2.75 8.25h14.5" />
      <circle cx="10" cy="12.5" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  '/schedule': (
    <>
      <rect x="2.75" y="4.25" width="14.5" height="13" rx="2" />
      <path d="M6.5 2.75v3M13.5 2.75v3M2.75 8.25h14.5M7.5 8.25v9M12.5 8.25v9M2.75 12.75h14.5" />
    </>
  ),
  '/enquiries': (
    <path d="M17.25 11.5a2.5 2.5 0 0 1-2.5 2.5H8.5l-3.75 3v-3h-.5a2.5 2.5 0 0 1-2.5-2.5v-6A2.5 2.5 0 0 1 4.25 3h10.5a2.5 2.5 0 0 1 2.5 2.5z" />
  ),
  /* A list with two of its lines ticked off, which is what a follow-up list is: things somebody
     said she would do, and the two she has done. Nothing else in the rail is a tick beside a rule,
     so it reads as itself at 20px beside the speech mark above it. */
  '/tasks': (
    <>
      <path d="M2.75 5.75 4 7l2.25-2.5" />
      <path d="M2.75 10.75 4 12l2.25-2.5" />
      <path d="M9.25 6h8M9.25 11h8M9.25 16h8" />
    </>
  ),
  '/clients': (
    <>
      <path d="M3 8.5 10 3l7 5.5" />
      <path d="M4.75 9.75v6.5a1 1 0 0 0 1 1h8.5a1 1 0 0 0 1-1v-6.5" />
    </>
  ),
  '/money': (
    <>
      <rect x="2.25" y="5" width="15.5" height="10" rx="2" />
      <circle cx="10" cy="10" r="2.25" />
      <path d="M5 8v4M15 8v4" />
    </>
  ),
  /* A signpost: one post and two boards pointing opposite ways, which is the question the report
     answers. Nothing else in the rail is a diagonal, so it reads as itself at 20px. */
  '/sources': (
    <>
      <path d="M10 2.75v14.5" />
      <path d="M10 5.25h5.25L17 7.25l-1.75 2H10" />
      <path d="M10 11h-5.25L3 13l1.75 2H10" />
    </>
  ),
  '/breaches': (
    <>
      <circle cx="10" cy="10" r="7.25" />
      <path d="M6.75 10.25 9 12.5l4.25-4.75" />
    </>
  ),
  '/crew': (
    <>
      <circle cx="7.75" cy="7" r="2.5" />
      <path d="M3 16.25c0-2.35 2.13-4.25 4.75-4.25s4.75 1.9 4.75 4.25" />
      <path d="M13.5 5.1a2.5 2.5 0 0 1 0 4.8" />
      <path d="M14.5 12.4c1.7.55 2.9 1.98 2.9 3.85" />
    </>
  ),
  '/settings': (
    <>
      <path d="M3 6h9M15.5 6H17M3 14h1.5M8 14h9" />
      <circle cx="13.75" cy="6" r="1.75" />
      <circle cx="6.25" cy="14" r="1.75" />
    </>
  ),
};

/* ------------------------------------------------------------------ *
 * The pieces every screen shares.
 * ------------------------------------------------------------------ */

/**
 * The bell. A 44px target that opens the notifications list, with the unread count on it when
 * there is one. The count is GOLD ON NAVY and never coral: coral in this interface means a gap
 * somebody has to close, and an unread message is not yet known to be one.
 */
function Bell({ unread, tone }: { unread: number; tone: 'navy' | 'cream' }) {
  const label = unread === 0 ? 'Notifications' : `Notifications, ${unread} unread`;
  return (
    <Link
      to="/notifications"
      aria-label={label}
      title={label}
      className={`relative inline-flex min-h-11 desk:min-h-9 min-w-11 desk:min-w-9 items-center justify-center rounded-lg border ${
        tone === 'navy' ? 'border-cream/25' : 'border-navy/25'
      }`}
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
        <path d="M12 2a1 1 0 0 1 1 1v1.07A6 6 0 0 1 18 10v4.2l1.7 2.55A1 1 0 0 1 18.87 18H5.13a1 1 0 0 1-.83-1.25L6 14.2V10a6 6 0 0 1 5-5.93V3a1 1 0 0 1 1-1Zm-2.45 17h4.9A2.5 2.5 0 0 1 12 21.5 2.5 2.5 0 0 1 9.55 19Z" />
      </svg>
      {unread > 0 ? (
        <span className="absolute -top-1.5 -right-1.5 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1 text-xs font-semibold text-navy">
          {unread > 99 ? '99+' : unread}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * The heading every screen starts with, so they all start the same way.
 *
 * Under the desktop shell it renders into the top bar and hides itself at md; with no shell around
 * it, or under a shell with no rail, it draws where it always did. A screen writes it once either
 * way, which is why no screen had to change when the rail arrived.
 */
export function ScreenTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  const publish = useContext(HeadingSlot);

  useEffect(() => {
    if (!publish) return;
    const mine: Heading = { title, subtitle };
    publish(mine);
    // Only clear what is still ours: on a route change React runs this cleanup before the next
    // screen's effect, but a stray order would otherwise blank the heading that just arrived.
    return () => publish((current) => (current === mine ? null : current));
  }, [publish, title, subtitle]);

  return (
    <div className={`mb-4 ${publish ? 'md:hidden' : ''}`}>
      <h1 className="font-serif text-2xl leading-tight">{title}</h1>
      {subtitle ? <p className="mt-1 text-sm text-navy/70">{subtitle}</p> : null}
    </div>
  );
}

/** A white-ish panel. Used for a client, a home, a person, a rate. */
export function Card({
  children,
  className = '',
  label,
}: {
  children: ReactNode;
  className?: string;
  /** The section's name to a screen reader, where its heading alone does not carry it. */
  label?: string;
}) {
  return (
    <section
      aria-label={label}
      className={`rounded-xl border border-navy/12 bg-parchment p-4 shadow-[0_1px_2px_rgba(22,41,63,0.06)] ${className}`}
    >
      {children}
    </section>
  );
}

/** A small word beside a name: a status, a role, a channel. Never the only carrier of meaning. */
export function Pill({
  children,
  tone = 'quiet',
}: {
  children: ReactNode;
  tone?: 'quiet' | 'gold' | 'sage' | 'coral';
}) {
  const tones = {
    quiet: 'border-navy/20 bg-navy/5 text-navy/80',
    gold: 'border-gold/40 bg-gold/15 text-navy',
    sage: 'border-sage/60 bg-sage/20 text-navy',
    coral: 'border-coral/50 bg-coral/12 text-navy',
  };
  return (
    <span
      className={`inline-block rounded-full border px-2 py-0.5 text-xs whitespace-nowrap ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/** A labelled fact. Renders nothing at all when there is nothing to say, which is the point. */
export function Detail({ label, value }: { label: string; value: ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="py-1">
      <dt className="text-xs tracking-wide text-navy/55 uppercase">{label}</dt>
      <dd className="text-sm whitespace-pre-line">{value}</dd>
    </div>
  );
}
